import { ConditionalFunctionString } from '../../conditions.types'

export const CUSTOM_PROPERTY_SUB_OPERATORS = ['EQUALS', 'NOT_EQUALS'] as const
export type CustomPropertySubOperator =
  (typeof CUSTOM_PROPERTY_SUB_OPERATORS)[number]

export const isCustomPropertySubOperator = (
  op: unknown
): op is CustomPropertySubOperator =>
  CUSTOM_PROPERTY_SUB_OPERATORS.includes(op as CustomPropertySubOperator)

/**
 * Prefix marking a CUSTOMER operator as a per-custom-property comparison. The
 * operator id is `CUSTOM_PROPERTY:<key>` — the key is everything after the
 * prefix, so keys containing `:` round-trip safely.
 */
export const CUSTOM_PROPERTY_OPERATOR_PREFIX = 'CUSTOM_PROPERTY:'

export const customPropertyOperatorId = (key: string): string =>
  `${CUSTOM_PROPERTY_OPERATOR_PREFIX}${key}`

export const isCustomPropertyOperatorId = (operatorId: string): boolean =>
  operatorId.startsWith(CUSTOM_PROPERTY_OPERATOR_PREFIX)

/** Returns the custom-property key for a `CUSTOM_PROPERTY:<key>` operator id, or undefined. */
export const customPropertyKeyFromOperatorId = (
  operatorId: string
): string | undefined =>
  isCustomPropertyOperatorId(operatorId)
    ? operatorId.slice(CUSTOM_PROPERTY_OPERATOR_PREFIX.length)
    : undefined

/**
 * Value stored on a CUSTOMER custom-property condition. The custom-property
 * key lives in the operator id (`CUSTOM_PROPERTY:<key>`); the value only
 * carries the equals/not-equals choice and the comparand.
 */
export type CustomPropertyConditionValue = {
  operator?: CustomPropertySubOperator
  value?: string
}

export const readCustomPropertyValue = (
  value: unknown
): CustomPropertyConditionValue => {
  if (typeof value === 'object' && value !== null) {
    return value as CustomPropertyConditionValue
  }
  return {}
}

/**
 * Factory producing the function string for a single custom-property operator.
 * `key` is baked in (it comes from the operator id); the condition value
 * carries the equals/not-equals sub-operator and the comparand. `property`
 * resolves to the customer's custom-properties record.
 *
 * `(<property> || {})` guards a missing record at runtime: EQUALS fails closed
 * when the key is absent (`undefined === "x"` → false), NOT_EQUALS passes
 * (`undefined !== "x"` → true).
 */
export const customProperty =
  (key: string): ConditionalFunctionString =>
  ({ property, value }) => {
    const { operator, value: comparand } = readCustomPropertyValue(value)
    const accessor = `(${property} || {})[${JSON.stringify(key)}]`
    const symbol = operator === 'NOT_EQUALS' ? '!==' : '==='
    return `${accessor} ${symbol} ${JSON.stringify(comparand ?? '')}`
  }
